<template>
  <div
    ref="windowscreen"
    class="windowscreen"
    :class="{ 'show-operator': showOperator }"
  >
    <div
      v-if="!deviceIsReady"
      class="window whitescreen"
    />

    <div
      v-for="(currentWindow, index) in windows"
      v-show="currentWindow.show"
      :key="index"
      class="window"
      :class="{ 'show-no-status': !currentWindow.state }"
    >
      <div
        class="window-data"
        :style="{ color: colors.windowNumber, backgroundColor: colors.windowNumberBg, fontFamily: `${fonts.windowNumber}, Inter, sans-serif` }"
      >
        <div
          ref="windowNumbers"
          class="number"
        >
          {{ currentWindow.number }}
        </div>
        <div
          v-if="currentWindow.operatorName"
          class="operator"
        >
          {{ currentWindow.operatorName }}
        </div>
      </div>

      <div
        v-if="currentWindow.state"
        class="status"
        :style="{ color: colors.status, backgroundColor: colors.statusBg, fontFamily: `${fonts.status}, Inter, sans-serif` }"
      >
        <span
          v-if="currentWindow.ticketNumber"
          :class="{ 'flickering': isFlickering }"
        >
          {{ currentWindow.ticketNumber }}
        </span>
        
        <Icon
          v-else
          :type="ICONS_BY_STATE[currentWindow.state]"
        />
      </div>
    </div>
  </div>
</template>


<script>
import FontService from '@/services/FontService';
import { parseJSON } from '@/helpers/utils';
import { WINDOW_TYPE, WINDOW_TYPE_TRANSLATIONS } from '@/constants/WindowType';
import EVENT_TYPE from '@/constants/EventType';
import Icon from './Icon';
import ICON_LIST from './WindowscreenIcons';

window.LEDInterface;

const DEVICE_STATE = Object.freeze({
  NOT_WORKING: 'not_working',
  IDLE: 'idle',
  CLIENTS_SERVICE: 'clients_service',
  DOCUMENTS_PROCESSING: 'documents_processing',
  BREAK: 'break',
  CUSTOM_TAG: 'custom_tag'
});

// Status (tag ID: 1 - idle, 2 - clients_service, 3 - documents_processing, 4 - break, other - custom)
const STATES_BY_TAG = [
  DEVICE_STATE.NOT_WORKING,
  DEVICE_STATE.IDLE,
  DEVICE_STATE.CLIENTS_SERVICE,
  DEVICE_STATE.DOCUMENTS_PROCESSING,
  DEVICE_STATE.BREAK
];

const ICONS_BY_STATE = Object.freeze({
  [DEVICE_STATE.NOT_WORKING]: ICON_LIST.CLOSE,
  [DEVICE_STATE.IDLE]: ICON_LIST.CHECK,
  [DEVICE_STATE.CLIENTS_SERVICE]: null,
  [DEVICE_STATE.DOCUMENTS_PROCESSING] : ICON_LIST.PAPERS,
  [DEVICE_STATE.BREAK]: ICON_LIST.CUP,
  [DEVICE_STATE.CUSTOM_TAG]: ICON_LIST.TAG
});

const LED_STATE = Object.freeze({
  RED: 'red',
  GREEN: 'green',
  YELLOW: 'yellow'
});

const CHANGE_WINDOW_DELAY = 10000;
const FLICKERING_DURATION = 4000;
const NUMBER_MIN_FONT_SIZE = 120;
const NUMBER_MARGIN = 40;
const VALID_STATUS_TYPE = 'servicing';

export default {
  components: {
    Icon
  },
  data() {
    return {
      DEVICE_STATE: DEVICE_STATE,
      ICON_LIST: ICON_LIST,
      ICONS_BY_STATE: ICONS_BY_STATE,
      fontService: new FontService(),
      colors: {
        windowNumber: '#1C1C1E',
        windowNumberBg: '#EFEFF4',
        status: '#FFFFFF',
        statusBg: '#34C759',
      },
      fonts: {
        windowNumber: null,
        status: null
      },
      windows: [],
      isFlickering: false,
      deviceIsReady: false,
      counter: 0,
      flickeringQueue: [],
      flickerTimer: null,
      windowsTimer: null,
      startFlickeringTime: 0,
      currentLEDState: null
    }
  },
  computed: {
    showOperator() {
      return this.$store.windowscreenData.operators_name_visible;
    }
  },
  mounted() {
    this.$initService.authorizeDeviceByUID(this.$store.deviceStartData.device.token);
    this.initStylesAndFonts();
    this.$nextTick(() => {
      this.fontsLoadingDone().then(() => {
        this.initWindows();
        this.websocketSubscribe();
      });
    });
  },
  destroyed() {
    if (typeof this.socketMessageUnsubscription === 'function') this.socketMessageUnsubscription();
    this.fontService.destroy();
  },
  methods: {
    initStylesAndFonts() {
      const firmStyle = this.$store.windowscreenData.firm_style;
      const companyFonts = this.$store.deviceStartData.firm_fonts;
      const customFontsArray = [];

      if (firmStyle) {
        if (firmStyle.window_board_window_number) this.colors.windowNumber = firmStyle.window_board_window_number;
        if (firmStyle.window_board_background_window_number) this.colors.windowNumberBg = firmStyle.window_board_background_window_number;
        if (firmStyle.window_board_status) this.colors.status = firmStyle.window_board_status;
        if (firmStyle.window_board_background_status) this.colors.statusBg = firmStyle.window_board_background_status;
      }

      const customFonts = this.fontService.getCustomFontsByFontIds(
        companyFonts,
        {
          normalFontId: firmStyle.font_normal,
          semiBoldFontId: firmStyle.font_semi_bold,
          boldFontId: firmStyle.font_bold
        }
      );

      if (customFonts.fontBold) {
        this.fonts.status = customFonts.fontBold.name;
        customFontsArray.push(customFonts.fontBold);
      }

      if (customFonts.fontSemiBold) {
        this.fonts.windowNumber = customFonts.fontSemiBold.name;
        customFontsArray.push(customFonts.fontSemiBold);
      }

      this.fontService.updateFonts(customFontsArray);
    },
    fontsLoadingDone() {
      return new Promise((resolve) => {
        if (!document.fonts) {
          resolve();
          return;
        }

        const onloadingdonePromise = new Promise((onloadingdoneResolve) => {
          document.fonts.onloadingdone = (fontFaceSetEvent) => {
            onloadingdoneResolve();
          };
        });

        const timerPromise = new Promise((timerResolve) => {
          setTimeout(() => timerResolve(), 5000);
        });

        Promise.race([onloadingdonePromise, timerPromise]).then(() => {
          document.fonts.onloadingdone = null;
          resolve();
        });
      });
    },
    initWindows() {
      const onlineUsers = this.$store.windowscreenData.online_users;
      const windowCurrentSession = this.$store.windowscreenData.window_current_session;
      const tickets = this.$store.windowscreenData.tickets;

      this.windows = this.$store.windowscreenData.windows.map((item, index) => {
        const numberParts = [];

        if (WINDOW_TYPE_TRANSLATIONS[item.type]) {
          const translatedWindowType = this.$languageService.translateFilter(WINDOW_TYPE_TRANSLATIONS[item.type]);
          numberParts.push(translatedWindowType);
        }

        if (item.number) {
          let numberStr = item.number.toString();
          if (numberStr.length === 1 && item.type !== WINDOW_TYPE.CABINET) {
            numberStr = `0${numberStr}`;
          }
          numberParts.push(numberStr);
        }

        const tagId = windowCurrentSession[item.id] ? windowCurrentSession[item.id].tag_id : 0;

        return {
          index,
          id: item.id,
          number: numberParts.join(' '),
          operatorName: this.getOperatorName(onlineUsers[item.id]),
          ticketNumber: this.getTicketNumber(tickets[item.id]),
          state: this.getStateByTagId(tagId),
          show: true
        };
      });

      this.$nextTick(() => this.checkFontSizes());
    },
    checkFontSizes() {
      const maxWidth = this.$refs.windowscreen.clientWidth - NUMBER_MARGIN;

      this.windows.forEach((item, index) => {
        const windowNumberRef = this.$refs.windowNumbers[index];

        if (windowNumberRef && windowNumberRef.clientWidth > maxWidth) {
          let fontSize = parseInt(getComputedStyle(windowNumberRef).getPropertyValue('font-size'));

          while (fontSize > NUMBER_MIN_FONT_SIZE && windowNumberRef.clientWidth > maxWidth) {
            windowNumberRef.style.fontSize = `${--fontSize}px`;
          }
        }
      });

      if (this.windows.length && this.$store.windowscreenData.is_enabled) {
        this.$nextTick(() => {
          this.setWindow(0);
          this.deviceIsReady = true;
        });
      }
    },
    setWindow(counter) {
      if (counter === undefined) {
        if (this.flickeringQueue.length) {
          this.counter = this.flickeringQueue.shift();
          this.startFlickering();
        } else {
          this.counter = this.counter + 1 < this.windows.length ? this.counter + 1 : 0;
        }
      } else {
        this.counter = counter;
      }

      this.windows.forEach((item) => item.show = false);
      this.windows[this.counter].show = true;
      this.updateLED();

      if (this.windows.length) this.updateWindowsQueue();
    },
    updateWindowsQueue(now = false) {
      let delay;
  
      if (now) {
        const flickeringTime = Date.now() - this.startFlickeringTime;
        delay = FLICKERING_DURATION - flickeringTime + 1000;
        if (delay < 0) delay = 0;
      } else {
        delay = this.flickeringQueue.length ? FLICKERING_DURATION : CHANGE_WINDOW_DELAY;
      }

      clearTimeout(this.windowsTimer);
      this.windowsTimer = setTimeout(() => this.setWindow(), delay);
    },
    startFlickering() {
      console.log('startFlickering');
      this.isFlickering = true;
      this.startFlickeringTime = Date.now();
      clearTimeout(this.flickerTimer);
      this.flickerTimer = setTimeout(() => this.isFlickering = false, FLICKERING_DURATION);
    },
    websocketSubscribe() {
      this.socketMessageUnsubscription = this.$eventEmitter.subscribe(this.$eventEmitter.eventTypes.SOCKETS_MESSAGE, (response) => {
        if (!response.payload || !response.payload.payload) return;

        const eventType = response.payload.event_type;

        parseJSON(response.payload.payload)
          .then((data) => {
            switch (eventType) {
            case EVENT_TYPE.OPERATOR_LOGIN:
              this.login(true, data);
              break;

            case EVENT_TYPE.OPERATOR_LOGOUT:
              this.login(false, data);
              break;

            case EVENT_TYPE.CHANGE_OPWORKSPACE_STATUS:
              this.changeState(data);
              break;

            case EVENT_TYPE.CALL_TICKET:
            case EVENT_TYPE.CALL_AGAIN_TICKET:
              this.addTicket(data);
              break;
  
            case EVENT_TYPE.REDIRECT_TICKET:
            case EVENT_TYPE.CLOSE_TICKET:
            case EVENT_TYPE.POSTPONE_TICKET:
            case EVENT_TYPE.CANCEL_TICKET:
              this.removeTicket(data);
              break;

            case EVENT_TYPE.RELOAD_DEVICE:
              break;
  
            default:
              console.error(`Нет обработчика для event_type = ${eventType}`, response);
              break;
            }
          });
      });

      this.socketReconnectUnsubscription = this.$eventEmitter.subscribe(this.$eventEmitter.eventTypes.SOCKETS_RECONNECT, (response) => {
        this.$initService.authorizeDeviceByUID(this.$store.deviceStartData.device.token);
      })
    },
    getStateByTagId(tagId) {
      return STATES_BY_TAG[tagId] || DEVICE_STATE.CUSTOM_TAG;
    },
    getOperatorName(user) {
      const names = [];
      if (user && this.showOperator) {
        if (user.name) names.push(user.name);
        if (user.surname) names.push(user.surname);
      }
      return names.length ? names.join(' ') : '';
    },
    getTicketNumber(data) {
      let ticketNumber = '';
      if (data && data.number) {
        const prefix = data.prefix || '';
        ticketNumber = prefix + data.number;
      }
      return ticketNumber;
    },
    login(loggedIn, data) {
      const targetWindow = this.getWindowById(data.window_id);

      if (targetWindow) {
        targetWindow.operatorName = loggedIn ? this.getOperatorName(data.user) : '';
        const newState = loggedIn ? DEVICE_STATE.IDLE : DEVICE_STATE.NOT_WORKING;
        this.updateWindowState(targetWindow, newState);
      }
    },
    changeState(data) {
      const targetWindow = this.getWindowById(data.window_id);
      const tagId = data.shift ? data.shift.tag_id : null;

      if (targetWindow && tagId) {
        this.updateWindowState(targetWindow, this.getStateByTagId(tagId));
      }
    },
    addTicket(data) {
      if (data.status_type === VALID_STATUS_TYPE) {
        const ticketNumber = this.getTicketNumber(data);
        const targetWindow = this.getWindowById(data.last_window_id);
  
        if (ticketNumber && targetWindow) {
          targetWindow.ticketNumber = ticketNumber;
  
          if (
            !this.flickeringQueue.includes(targetWindow.index) &&
            !(this.isFlickering && this.counter === targetWindow.index)
          ) {
            this.flickeringQueue.push(targetWindow.index);
            this.updateWindowsQueue(true);
          }
        }
      }
    },
    removeTicket(data) {
      let windowId = data.last_window_id;

      if (!windowId && data.ticket) {
        windowId = data.ticket.last_window_id;
      }

      const targetWindow = this.getWindowById(windowId);
      if (targetWindow) {
        targetWindow.ticketNumber = '';
      }
    },
    getWindowById(windowId) {
      return this.windows.find((item) => item.id === windowId);
    },
    updateWindowState(targetWindow, newState) {
      targetWindow.state = newState;
      this.updateLED();
    },
    updateLED() {
      if (typeof window.LEDInterface !== 'undefined' && window.LEDInterface !== null) {
        const currentWindow = this.windows[this.counter];
        let newLEDState = null;

        if (currentWindow.ticketNumber) {
          newLEDState = LED_STATE.YELLOW;
        } else {
          switch (currentWindow.state) {
          case DEVICE_STATE.NOT_WORKING:
          case DEVICE_STATE.BREAK:
          case DEVICE_STATE.DOCUMENTS_PROCESSING:
            newLEDState = LED_STATE.RED;
            break;
  
          case DEVICE_STATE.IDLE:
            newLEDState = LED_STATE.GREEN;
            break;
            
          case DEVICE_STATE.CLIENTS_SERVICE:
            newLEDState = LED_STATE.YELLOW;
            break;
          }
        }

        if (this.currentLEDState !== newLEDState) {
          this.currentLEDState = newLEDState;

          switch (this.currentLEDState) {
          case LED_STATE.RED:
            window.LEDInterface.changeColor('1', '255', '0', '0');
            break;
  
          case LED_STATE.GREEN:
            window.LEDInterface.changeColor('1', '0', '255', '0');
            break;
            
          case LED_STATE.YELLOW:
            window.LEDInterface.changeColor('1', '255', '255', '0');
            break;
          }
        }
      }
    }
  }
}
</script>


<style scoped lang="scss">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.windowscreen {
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-feature-settings: 'zero' on;
  line-height: 1.28;
  position: relative;
  height: 100vh;
}

.whitescreen {
  background-color: white;
  z-index: 10;
}

.window {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.window-data {
  height: 300px;
  flex-shrink: 0;
  font-size: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .number {
    min-height: 230px;
  }
}

.show-operator {
  .window-data {
    height: 400px;
  }
}

.show-no-status {
  .window-data {
    height: 100%;
    font-size: 260px;
  }
}

.number,
.status {
  display: flex;
  align-items: center;
  justify-content: center;
}

.number,
.status span {
  white-space: nowrap;
}

.operator {
  max-height: 2em;
  max-width: 100%;
  overflow: hidden;
  font-size: 60px;
  line-height: 60px;
  text-align: center;
  margin-top: 15px;
  padding: 0 40px;
}

.status {
  flex-grow: 1;
  font-weight: bold;
  font-size: 300px;
}

@keyframes flickerAnimation {
  0%   { opacity: 1; }
  50%  { opacity: 0; }
  100% { opacity: 1; }
}

.flickering {
  animation: linear 1.3s 3 flickerAnimation;
}
</style>
